import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {omit} from 'ramda';

import {TabPanel as DXTabPanel} from 'devextreme-react';


export default class TabPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedIndex: this.props.selectedIndex
        };

        this.itemTitleTemplate = this.itemTitleTemplate.bind(this);
        this.itemTemplate = this.itemTemplate.bind(this);
        this.onOptionChanged = this.onOptionChanged.bind(this);
    }

    itemTitleTemplate(template_id) {
        var tmpl = window[template_id];
        return function(itemData, itemIndex, element) {
            ReactDOM.render(tmpl(itemData), element);
        }
    }

    itemTemplate(template_id) {
        var tmpl = window[template_id];
        return function(itemData, itemIndex, element) {
            ReactDOM.render(tmpl(itemData), element);
        }
    }

    onOptionChanged(e) {
        if(e.name === 'selectedIndex') {
            this.setState({selectedIndex: e.value});
        }
    }

    render() {
        const dataAttributes = {};
        if (this.props.loading_state && this.props.loading_state.is_loading) {
            dataAttributes['data-dash-is-loading'] = true;
        }

        let {itemTitleTemplate, itemTemplate} = this.props;

        if (itemTitleTemplate && typeof itemTitleTemplate === 'string' && itemTitleTemplate !== 'title'){
            itemTitleTemplate = this.itemTitleTemplate(itemTitleTemplate);
        }

        if (itemTemplate && typeof itemTemplate === 'string' && itemTemplate !== 'item'){
            itemTemplate = this.itemTemplate(itemTemplate);
        }

        return (
            <DXTabPanel
                {...omit(['loading_state', 'setProps'], this.props)}
                {...dataAttributes}
                itemTitleTemplate={itemTitleTemplate}
                itemTemplate={itemTemplate}
                selectedIndex={this.state.selectedIndex}
                onOptionChanged ={this.onOptionChanged}
            />
        )
    }
}

TabPanel.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks
     */
    id: PropTypes.string,

    /**
     * Specifies the shortcut key that sets focus on the UI component
     */
    accessKey: PropTypes.string,
    /**
     * Specifies whether or not the UI component changes its state when interacting with a user
     */
    activeStateEnabled: PropTypes.bool,
    /**
     * Specifies whether or not to animate the displayed item change
     */
    animationEnabled: PropTypes.bool,
    /**
     * Binds the UI component to data
     */
    dataSource: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
    /**
     * Specifies whether to render the view's content when it is displayed. If false, the content is rendered immediately
     */
    deferRendering: PropTypes.bool,
    /**
     * Specifies whether the UI component responds to user interaction
     */
    disabled: PropTypes.bool,
    /**
     * Specifies the global attributes to be attached to the UI component's container element
     */
    elementAttr: PropTypes.object,
    /**
     * Specifies whether the UI component can be focused using keyboard navigation
     */
    focusStateEnabled: PropTypes.bool,
    /**
     * Specifies the UI component's height
     */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),
    /**
     * Specifies text for a hint that appears when a user pauses on the UI component
     */
    hint: PropTypes.string,
    /**
     * Specifies whether the UI component changes its state when a user pauses on it
     */
    hoverStateEnabled: PropTypes.bool,
    /**
     * An alias for the itemTemplate property specified in React. Accepts a custom component.
     */
    itemComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /**
     * The time period in milliseconds before the onItemHold event is raised
     */
    itemHoldTimeout: PropTypes.number,
    /**
     * An alias for the itemTemplate property specified in React. Accepts a rendering function.
     */
    itemRender: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * An array of items displayed by the UI component
     */
    items: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.object)]),
    /**
     * Specifies a custom template for items
     */
    itemTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    /**
     * An alias for the itemTitleTemplate property specified in React. Accepts a custom component.
     */
    itemTitleComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /**
     * An alias for the itemTitleTemplate property specified in React. Accepts a rendering function.
     */
    itemTitleRender: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * Specifies a custom template for item titles
     */
    itemTitleTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    /**
     * A Boolean value specifying whether or not to scroll back to the first item after the last item is swiped
     */
    loop: PropTypes.bool,
    /**
     * The text or HTML markup displayed by the UI component if the item collection is empty
     */
    noDataText: PropTypes.string,
    /**
     * A function that is executed when the UI component's content is ready and each time the content is changed
     */
    onContentReady: PropTypes.func,
    /**
     * A function that is executed before the UI component is disposed of
     */
    onDisposing: PropTypes.func,
    /**
     * A function used in JavaScript frameworks to save the UI component instance
     */
    onInitialized: PropTypes.func,
    /**
     * A function that is executed when a collection item is clicked or tapped
     */
    onItemClick: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /**
     * A function that is executed when a collection item is right-clicked or pressed
     */
    onItemContextMenu: PropTypes.func,
    /**
     * A function that is executed when a collection item has been held for a specified period
     */
    onItemHold: PropTypes.func,
    /**
     * A function that is executed after a collection item is rendered
     */
    onItemRendered: PropTypes.func,
    /**
     * A function that is executed after a UI component property is changed
     */
    onOptionChanged: PropTypes.func,
    /**
     * A function that is executed when a collection item is selected or selection is canceled
     */
    onSelectionChanged: PropTypes.func,
    /**
     * A function that is executed when a tab is clicked or tapped
     */
    onTitleClick: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /**
     * A function that is executed when a tab has been held for a specified period
     */
    onTitleHold: PropTypes.func,
    /**
     * A function that is executed after a tab is rendered
     */
    onTitleRendered: PropTypes.func,
    /**
     * Specifies whether to repaint only those elements whose data changed
     */
    repaintChangesOnly: PropTypes.bool,
    /**
     * Switches the UI component to a right-to-left representation
     */
    rtlEnabled: PropTypes.bool,
    /**
     * A Boolean value specifying if tabs in the title are scrolled by content
     */
    scrollByContent: PropTypes.bool,
    /**
     * A Boolean indicating whether or not to add scrolling support for tabs in the title
     */
    scrollingEnabled: PropTypes.bool,
    /**
     * The index of the currently displayed item
     */
    selectedIndex: PropTypes.number,
    /**
     * The selected item object
     */
    selectedItem: PropTypes.object,
    /**
     * Specifies whether navigation buttons should be available when tabs exceed the UI component's width
     */
    showNavButtons: PropTypes.bool,
    /**
     * A Boolean value specifying whether or not to allow users to change the selected index by swiping
     */
    swipeEnabled: PropTypes.bool,
    /**
     * Specifies the number of the element when the Tab key is used for navigating
     */
    tabIndex: PropTypes.number,
    /**
     * Specifies whether the UI component is visible
     */
    visible: PropTypes.bool,
    /**
     * Specifies the UI component's width
     */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),

    /**
     * Object that holds the loading state object coming from dash-renderer
     */
    loading_state: PropTypes.shape({
        /**
         * Determines if the component is loading or not
         */
        is_loading: PropTypes.bool,
        /**
         * Holds which property is loading
         */
        prop_name: PropTypes.string,
        /**
         * Holds the name of the component that is loading
         */
        component_name: PropTypes.string,
    }),
    /**
     * Dash-assigned callback that gets fired when the value changes.
     */
    setProps: PropTypes.func
};

TabPanel.defaultProps = {
    accessKey: null,
    activeStateEnabled: false,
    animationEnabled: true,
    dataSource: null,
    deferRendering: true,
    disabled: false,
    elementAttr: {},
    focusStateEnabled: false,
    height: 'auto',
    // hint: undefined,
    hoverStateEnabled: true,
    itemComponent: null,
    itemHoldTimeout: 750,
    itemRender: null,
    items: undefined,
    itemTemplate: 'item',
    itemTitleComponent: null,
    itemTitleRender: null,
    itemTitleTemplate: 'title',
    loop: false,
    noDataText: 'No data to display',
    onContentReady: null,
    onDisposing: null,
    onInitialized: null,
    onItemClick: null,
    onItemContextMenu: null,
    onItemHold: null,
    onItemRendered: null,
    onOptionChanged: null,
    onSelectionChanged: null,
    onTitleClick: null,
    onTitleHold: null,
    onTitleRendered: null,
    repaintChangesOnly: false,
    rtlEnabled: false,
    scrollByContent: true,
    scrollingEnabled: true,
    selectedIndex: 0,
    selectedItem: null,
    showNavButtons: false,
    swipeEnabled: true,
    tabIndex: 0,
    visible: true,
    width: 'auto'
};
